/**
 * Event log panel — the visible combat log and, in the same element, the
 * §6.2 `[data-testid="log"]` mirror (`data-event-id` entries carrying
 * `data-kind`). Single element for both jobs, same rule as the seat list
 * and turn clock: the mirror can never disagree with what the table sees.
 *
 * Contract (PO ruling, 2026-07-18): the log lives in RoomState as a capped
 * ring buffer (~200 events), synced and persisted; a reconnecting player
 * receives the buffer. The schema field is the Lead's M3 work — until it
 * lands, this module renders from a panel-level view model. Each entry's
 * text must be renderable from the event alone (EFX-21: no lookups into
 * state that may have moved on), so the view model carries the final text.
 *
 * Pure render, idempotent, no net/ coupling.
 */

/** One log line. `kind` is an open set — "expiry" | "removal" | "retarget"
 *  today (§6.2), more as the engine grows; the mirror passes it through
 *  verbatim rather than gatekeeping. */
export interface LogEvent {
  id: string;
  kind: string;
  text: string;
}

/**
 * Render the log into `root`. Events render oldest → newest, matching ring
 * buffer order; the container is `role="log"` so screen readers announce
 * additions politely. The container renders even when empty — E2E counts
 * entries (E2E-07: "exactly 1 expiry"), so the surface must exist before the
 * first event does.
 */
export function renderLogPanel(root: HTMLElement, events: readonly LogEvent[]): void {
  root.replaceChildren();
  root.dataset["testid"] = "log";
  root.setAttribute("role", "log");
  root.setAttribute("aria-label", "Table log");

  for (const event of events) {
    const entry = document.createElement("p");
    entry.className = "log-entry";
    entry.dataset["eventId"] = event.id;
    entry.dataset["kind"] = event.kind;
    entry.textContent = event.text;
    root.appendChild(entry);
  }
}
